import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [f, setF] = useState({ email: "", code: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (e, fn) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  const sendCode = (e) => run(e, async () => {
    await api.post("/auth/forgot-password", { email: f.email });
    setStep("code");
  });
  const reset = (e) => run(e, async () => {
    await api.post("/auth/reset-password", f);
    toast.success("Password updated. Please sign in with your new password.");
    nav("/login");
  });

  return (
    <div className="min-h-screen grid place-items-center bg-[#080C14] px-6" data-testid="forgot-page">
      <form onSubmit={step === "email" ? sendCode : reset} className="w-full max-w-sm space-y-4 fade-up" data-testid="forgot-form">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="forgot-back-link"><ArrowLeft className="h-4 w-4" />Back to sign in</Link>
        <h1 className="text-3xl font-bold">Forgot password</h1>
        {step === "email" ? (
          <>
            <p className="text-sm text-slate-400">Enter your account email and we'll send you a reset code.</p>
            <Input type="email" required value={f.email} onChange={set("email")} placeholder="Email" className="bg-[#0F172A]" data-testid="forgot-email-input" />
          </>
        ) : (
          <>
            <p className="text-sm text-slate-400" data-testid="forgot-code-sent">If an account exists for {f.email}, a reset code is on its way. Check your inbox (and junk folder).</p>
            <Input required value={f.code} onChange={set("code")} placeholder="Reset code (e.g. ABCD-2345)" className="bg-[#0F172A] font-mono uppercase" data-testid="forgot-code-input" />
            <PasswordInput required minLength={6} value={f.password} onChange={set("password")} placeholder="New password" autoComplete="new-password" className="bg-[#0F172A]" data-testid="forgot-new-password-input" />
          </>
        )}
        {error && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2" data-testid="forgot-error">{error}</div>}
        <Button type="submit" disabled={busy} className="w-full h-11 font-semibold" data-testid="forgot-submit">
          {busy ? "Please wait..." : step === "email" ? "Send reset code" : "Set new password"}
        </Button>
        {step === "code" && <button type="button" onClick={() => setStep("email")} className="text-xs text-slate-400 hover:text-white" data-testid="forgot-resend">Didn't get it? Send a new code</button>}
      </form>
    </div>
  );
}
